import {
  Box,
  BoxProps,
  RecipeVariantProps,
  useSlotRecipe
} from "@chakra-ui/react"
import {
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState
} from "react"
import { placeholderRecipe } from "./placeholder.recipe"

type PlaceholderStyles = ReturnType<ReturnType<typeof useSlotRecipe>>

type PlaceholderContextValue = {
  styles: PlaceholderStyles
  labelId: string
  hintId: string
  setHasLabel: (hasLabel: boolean) => void
  setHasHint: (hasHint: boolean) => void
}

const PlaceholderContext = createContext<PlaceholderContextValue | null>(null)

const usePlaceholderContext = () => {
  const context = useContext(PlaceholderContext)
  if (!context)
    throw new Error("Placeholder parts must render inside <Placeholder.Root>")
  return context
}

const usePlaceholderStyles = () => usePlaceholderContext().styles

type PlaceholderVariantProps = RecipeVariantProps<typeof placeholderRecipe>

export type PlaceholderRootProps = BoxProps &
  PlaceholderVariantProps & {
    /** `light` for page surfaces, `onImage` over photos and color fills. */
    tone?: "light" | "onImage"
    /**
     * `box` for a card, row, or block of content. `pill` for a missing button,
     * `text` for a missing line of text inside an otherwise real row, and
     * `icon` for a round icon-only slot (give it an `aria-label`).
     */
    shape?: "box" | "pill" | "icon" | "text"
    /**
     * `action` invites the person to add something. `notice` explains what
     * has to happen first, in quieter text.
     */
    variant?: "action" | "notice"
    /** Where the media and label sit inside a `box`. */
    align?: "start" | "center"
  }

/**
 * The empty slot, and the control that fills it: the whole slot is one click
 * target. Render it as a button or link with `asChild`, and size and shape it
 * like the content that will fill it.
 */
const PlaceholderRoot = forwardRef<HTMLDivElement, PlaceholderRootProps>(
  function PlaceholderRoot(
    { tone, shape, variant, align, children, ...rest },
    ref
  ) {
    const recipe = useSlotRecipe({ recipe: placeholderRecipe })
    const styles = recipe({ tone, shape, variant, align })
    const id = useId()
    const labelId = `${id}-label`
    const hintId = `${id}-hint`
    const [hasLabel, setHasLabel] = useState(false)
    const [hasHint, setHasHint] = useState(false)
    const context = useMemo(
      () => ({ styles, labelId, hintId, setHasLabel, setHasHint }),
      [styles, labelId, hintId]
    )
    return (
      <PlaceholderContext.Provider value={context}>
        {/* The label names the control; the hint describes it. */}
        <Box
          ref={ref}
          data-placeholder=""
          aria-labelledby={hasLabel ? labelId : undefined}
          aria-describedby={hasHint ? hintId : undefined}
          css={styles.root}
          {...rest}
        >
          {children}
        </Box>
      </PlaceholderContext.Provider>
    )
  }
)

/** A shape standing in for an image, avatar, or icon. */
const PlaceholderMedia = forwardRef<HTMLDivElement, BoxProps>(
  function PlaceholderMedia(props, ref) {
    const styles = usePlaceholderStyles()
    return <Box ref={ref} aria-hidden css={styles.media} {...props} />
  }
)

/** Groups the label and hint into one block of text beside any media. */
const PlaceholderBody = forwardRef<HTMLSpanElement, BoxProps>(
  function PlaceholderBody(props, ref) {
    const styles = usePlaceholderStyles()
    return <Box as="span" ref={ref} css={styles.body} {...props} />
  }
)

/**
 * The slot's few words, e.g. an icon and "Add Offer". It names the control,
 * so say what it adds. Longer guidance belongs in a tooltip or a Tip.
 */
const PlaceholderLabel = forwardRef<HTMLSpanElement, BoxProps>(
  function PlaceholderLabel(props, ref) {
    const { styles, labelId, setHasLabel } = usePlaceholderContext()
    useEffect(() => {
      setHasLabel(true)
      return () => setHasLabel(false)
    }, [setHasLabel])
    return (
      <Box as="span" ref={ref} id={labelId} css={styles.label} {...props} />
    )
  }
)

/**
 * One line on why the slot matters. Collapsed until the slot is hovered or
 * focused, and always shown on touch screens. Use it in a `box`; a `pill` or
 * `text` slot has no room for one.
 */
const PlaceholderHint = forwardRef<HTMLSpanElement, BoxProps>(
  function PlaceholderHint(props, ref) {
    const { styles, hintId, setHasHint } = usePlaceholderContext()
    useEffect(() => {
      setHasHint(true)
      return () => setHasHint(false)
    }, [setHasHint])
    const { children, ...rest } = props
    return (
      <Box as="span" ref={ref} id={hintId} css={styles.hint} {...rest}>
        {/* The inner element is what the collapsing row clips. */}
        <span>{children}</span>
      </Box>
    )
  }
)

export const Placeholder = {
  Root: PlaceholderRoot,
  Media: PlaceholderMedia,
  Body: PlaceholderBody,
  Label: PlaceholderLabel,
  Hint: PlaceholderHint
}

export default Placeholder
